import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Save, Send, CheckCircle,
  FileText, AlertCircle, Trophy, BarChart3, MessageSquare,
  Star, Award
} from 'lucide-react';

const EvaluatorScoringPage = () => {
  const { assessmentId, organizationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState([]);
  const [orgResponses, setOrgResponses] = useState({});
  const [scores, setScores] = useState({});
  const [currentPillar, setCurrentPillar] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assignment, setAssignment] = useState(location.state?.assignment || null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const evaluatorId = localStorage.getItem('user_id');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all questions
      const questionsResponse = await fetch(`${BACKEND_URL}/api/assessments/questions`);
      const questionsData = await questionsResponse.json();
      const activeQuestions = Array.isArray(questionsData) 
        ? questionsData.filter(q => q.is_active) 
        : [];
      setQuestions(activeQuestions);

      // Fetch organization responses
      try {
        const responsesResponse = await fetch(`${BACKEND_URL}/api/organization-responses/${organizationId}`);
        if (responsesResponse.ok) {
          const responsesData = await responsesResponse.json();
          const responsesMap = {};
          const responsesList = Array.isArray(responsesData) ? responsesData : (responsesData.responses || []);
          responsesList.forEach(r => {
            responsesMap[r.question_id] = r;
          });
          setOrgResponses(responsesMap);
        }
      } catch (e) {
        console.log('No responses found for organization');
      }

      // Fetch existing scores if any
      try {
        const scoresResponse = await fetch(`${BACKEND_URL}/api/evaluator-scores/${organizationId}/${evaluatorId}`);
        if (scoresResponse.ok) {
          const scoresData = await scoresResponse.json();
          const scoresMap = {};
          const scoresList = Array.isArray(scoresData) ? scoresData : (scoresData.scores || []);
          scoresList.forEach(s => {
            scoresMap[s.question_id] = s;
          });
          setScores(scoresMap);
        }
      } catch (e) {
        console.log('No existing scores found');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pillarQuestions = questions.filter(q => q.pillar_number === currentPillar);
  const currentQuestion = pillarQuestions[currentQuestionIndex];

  // Rubric level scoring
  const rubricLevels = [
    { key: '5', label: 'Exemplary', score: 100, color: 'green', description: 'Outstanding performance' },
    { key: '4', label: 'Advanced', score: 80, color: 'blue', description: 'Strong performance' },
    { key: '3', label: 'Established', score: 60, color: 'yellow', description: 'Meeting expectations' },
    { key: '2', label: 'Developing', score: 40, color: 'orange', description: 'Needs improvement' },
    { key: '1', label: 'Beginning', score: 20, color: 'red', description: 'Significant gaps' },
  ];

  const handleScoreSelect = (level) => {
    if (!currentQuestion) return;
    
    const rubric = currentQuestion.rubric?.[level.key] || {};
    
    setScores(prev => ({
      ...prev,
      [currentQuestion.question_id]: {
        ...prev[currentQuestion.question_id],
        question_id: currentQuestion.question_id,
        selected_level: level.key,
        score: level.score,
        level_label: level.label,
        rubric_description: rubric.description || level.description
      }
    }));
  };

  const handleSaveScore = async () => {
    if (!currentQuestion) return;

    const questionScore = scores[currentQuestion.question_id];
    if (!questionScore?.selected_level) {
      alert('Please select a score level before saving');
      return;
    }

    if (!questionScore.evaluator_remarks?.trim()) {
      alert('Evaluator remarks are required before saving');
      return;
    }

    setSaving(true);
    try {
      const scoreData = {
        assessment_id: assessmentId,
        organization_id: organizationId,
        evaluator_id: evaluatorId,
        question_id: currentQuestion.question_id,
        pillar_number: currentQuestion.pillar_number,
        score: questionScore.score,
        selected_level: questionScore.selected_level,
        level_label: questionScore.level_label,
        rubric_description: questionScore.rubric_description,
        evaluator_remarks: questionScore.evaluator_remarks,
        question_text: currentQuestion.question_text,
        scored_at: new Date().toISOString()
      };

      const response = await fetch(`${BACKEND_URL}/api/evaluator-scoring/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scoreData)
      });

      if (response.ok) {
        // Move to next question
        if (currentQuestionIndex < pillarQuestions.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else if (currentPillar < 8) {
          setCurrentPillar(currentPillar + 1);
          setCurrentQuestionIndex(0);
        } else {
          alert('All questions scored! Evaluation complete.');
        }
      }
    } catch (error) {
      console.error('Error saving score:', error);
      alert('Error saving score. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getCompletionStats = () => {
    let scored = 0;
    questions.forEach(q => {
      if (scores[q.question_id]?.selected_level) {
        scored++;
      }
    });
    return { scored, total: questions.length, percentage: questions.length > 0 ? Math.round((scored / questions.length) * 100) : 0 };
  };

  const stats = getCompletionStats();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading assessment questions...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Questions Found</h2>
          <p className="text-gray-600 mb-6">Unable to load assessment questions.</p>
          <button
            onClick={() => navigate('/evaluator/dashboard')}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Pillar Complete!</h2>
          <p className="text-gray-600 mb-6">You've reviewed all questions in Pillar {currentPillar}.</p>
          {currentPillar < 8 ? (
            <button
              onClick={() => { setCurrentPillar(currentPillar + 1); setCurrentQuestionIndex(0); }}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700"
            >
              Continue to Pillar {currentPillar + 1}
            </button>
          ) : (
            <button
              onClick={() => navigate('/evaluator/dashboard')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
            >
              Complete & Return to Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }

  const orgResponse = orgResponses[currentQuestion.question_id];
  const currentScore = scores[currentQuestion.question_id];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white sticky top-0 z-10 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/evaluator/dashboard')}
                className="p-2 hover:bg-white/20 rounded-lg transition-all"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-black">Evaluation Scoring</h1>
                <p className="text-purple-200 text-sm">{assignment?.organization_name || 'Organization Assessment'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-purple-200">Progress</p>
              <p className="text-2xl font-bold">{stats.scored}/{stats.total}</p>
              <div className="w-32 bg-white/20 rounded-full h-2 mt-1">
                <div className="bg-white h-2 rounded-full transition-all" style={{ width: `${stats.percentage}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Pillars */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-4 sticky top-24">
              <h3 className="font-bold text-gray-900 mb-4 text-sm">6 Pillars</h3>
              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(pillar => {
                  const pillarQs = questions.filter(q => q.pillar_number === pillar);
                  const completed = pillarQs.filter(q => scores[q.question_id]?.selected_level).length;
                  
                  return (
                    <button
                      key={pillar}
                      onClick={() => { setCurrentPillar(pillar); setCurrentQuestionIndex(0); }}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-all text-sm ${
                        currentPillar === pillar
                          ? 'bg-purple-600 text-white shadow-lg'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`pillar-btn-${pillar}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">Pillar {pillar}</span>
                        <span className="text-xs">{completed}/{pillarQs.length}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Scoring Area */}
          <div className="col-span-9">
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">
              {/* Question Header */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold opacity-80">
                    Question {currentQuestionIndex + 1} of {pillarQuestions.length}
                  </span>
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold">
                    Pillar {currentPillar}: {currentQuestion.pillar_name || ''}
                  </span>
                </div>
                <h2 className="text-xl font-bold">{currentQuestion.question_text}</h2>
                <p className="text-purple-200 text-sm mt-2">ID: {currentQuestion.question_id}</p>
              </div>

              {/* Organization Response */}
              <div className="p-6 bg-blue-50 border-b border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="font-bold text-gray-900">Organization's Response:</h3>
                </div>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  {orgResponse ? (
                    <div>
                      <p className="text-gray-900">{orgResponse.response_text || orgResponse.selected_option || 'Response recorded'}</p>
                      {orgResponse.additional_notes && (
                        <p className="text-gray-600 text-sm mt-2 italic">Notes: {orgResponse.additional_notes}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-400 italic">Organization has not responded to this question yet.</p>
                  )}
                </div>
              </div>

              {/* Scoring Section */}
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-600" />
                  Select Score Level:
                </h3>
                
                {/* Rubric Level Buttons */}
                <div className="grid grid-cols-5 gap-3 mb-6">
                  {rubricLevels.map((level) => {
                    const isSelected = currentScore?.selected_level === level.key;
                    const rubric = currentQuestion.rubric?.[level.key] || {};
                    
                    return (
                      <button
                        key={level.key}
                        onClick={() => handleScoreSelect(level)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          isSelected
                            ? `bg-${level.color}-100 border-${level.color}-500 ring-4 ring-${level.color}-200 shadow-lg`
                            : `border-gray-200 hover:border-${level.color}-300 hover:bg-${level.color}-50`
                        }`}
                        style={{
                          backgroundColor: isSelected ? `var(--${level.color}-100, #f0fdf4)` : undefined,
                          borderColor: isSelected ? `var(--${level.color}-500, #22c55e)` : undefined
                        }}
                        data-testid={`score-level-${level.key}`}
                      >
                        <div className="text-center">
                          <div className={`text-2xl font-black mb-1 ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                            {level.score}
                          </div>
                          <p className={`text-sm font-bold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                            {rubric.label || level.label}
                          </p>
                          <p className={`text-xs mt-1 ${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>
                            Level {level.key}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Selected Level Description */}
                {currentScore?.selected_level && (
                  <div className="mb-6 p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <p className="text-sm font-semibold text-purple-900 mb-1">
                      Selected: {currentScore.level_label} ({currentScore.score} points)
                    </p>
                    <p className="text-sm text-purple-700">
                      {currentQuestion.rubric?.[currentScore.selected_level]?.description || currentScore.rubric_description}
                    </p>
                  </div>
                )}

                {/* Evaluator Remarks */}
                <div className="p-6 bg-amber-50 rounded-xl border-2 border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-5 h-5 text-amber-600" />
                    <h4 className="font-bold text-amber-900">Evaluator Remarks</h4>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Required</span>
                  </div>
                  <textarea
                    value={currentScore?.evaluator_remarks || ''}
                    onChange={(e) => {
                      setScores(prev => ({
                        ...prev,
                        [currentQuestion.question_id]: {
                          ...prev[currentQuestion.question_id],
                          evaluator_remarks: e.target.value
                        }
                      }));
                    }}
                    placeholder="Provide detailed feedback for this question. Explain your scoring decision and any recommendations..."
                    className="w-full h-32 p-4 border-2 border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                    data-testid="evaluator-remarks"
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="p-6 bg-gray-50 border-t flex items-center justify-between">
                <button
                  onClick={() => {
                    if (currentQuestionIndex > 0) {
                      setCurrentQuestionIndex(currentQuestionIndex - 1);
                    } else if (currentPillar > 1) {
                      setCurrentPillar(currentPillar - 1);
                      const prevPillarQs = questions.filter(q => q.pillar_number === currentPillar - 1);
                      setCurrentQuestionIndex(prevPillarQs.length - 1);
                    }
                  }}
                  disabled={currentPillar === 1 && currentQuestionIndex === 0}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="prev-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Previous
                </button>

                <button
                  onClick={handleSaveScore}
                  disabled={saving || !currentScore?.selected_level}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-bold hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  data-testid="save-next-btn"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Save & Next
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluatorScoringPage;
